import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Alert,
  Table,
  InputGroup
} from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
  FaBolt,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaTimes,
  FaSearch,
  FaFilter
} from 'react-icons/fa';
import axios from 'axios';
import DataApi from '../../api/DataApi';
import { crmAuthHeaders } from '../../api/DataApi';
import { API_BASE_URL } from '../../constants/CONSTANT';
import { filterObjectManagerAllowedObjects } from '../../utils/filterObjectManagerAllowedObjects';
import { getEntitlementsSection } from '../../utils/entitlements';
import { fetchUiComponentRegistryRows } from '../layout/ComponentRegistry';
import SalesforceToast from '../common/SalesforceToast';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import './css/ActionManager.css';

/** Stored action_type → list/form label (same as Form.Select options). */
const ACTION_TYPE_LABELS = {
  row_action: 'Row Action',
  list_action: 'List Action',
  detail_action: 'Detail Action',
  edit_action: 'Edit Action',
  bulk_action: 'Bulk Action',
  global_action: 'Global Action',
  // legacy / alternate keys
  row: 'Row Action',
  bulk: 'Bulk Action',
  page: 'Page Action',
  global: 'Global Action',
};

/** Stored action_behavior → label */
const ACTION_BEHAVIOR_LABELS = {
  new: 'New',
  edit: 'Edit',
  delete: 'Delete',
  import: 'Import',
  export: 'Export',
  url: 'URL',
  component: 'Component',
  custom_script: 'Custom Script',
};

function labelForActionType(type) {
  const k = String(type || '').trim().toLowerCase();
  if (!k) return '—';
  return ACTION_TYPE_LABELS[k] || type;
}

function labelForActionBehavior(behavior) {
  const k = String(behavior || '').trim().toLowerCase();
  if (!k) return '—';
  return ACTION_BEHAVIOR_LABELS[k] || behavior;
}

/** Registry propSpec.type: field = object field dropdown; text = static string. */
function propSpecSourceType(spec) {
  const t = spec?.type;
  if (t === 'text') return 'text';
  return 'field';
}

/** Form draft string from stored propMappings entry (legacy string or { sourceType, value }). */
function propMappingEntryToDraftValue(entry) {
  if (entry == null || entry === '') return '';
  if (typeof entry === 'string') return entry;
  if (typeof entry === 'object' && entry.value != null) return String(entry.value);
  return '';
}

function propMappingsToDraft(propMappings) {
  if (!propMappings || typeof propMappings !== 'object' || Array.isArray(propMappings)) {
    return {};
  }
  const draft = {};
  Object.entries(propMappings).forEach(([k, v]) => {
    draft[k] = propMappingEntryToDraftValue(v);
  });
  return draft;
}

/** Active custom_script rows for Action Manager; default object-scoped only (matchObjectOnly). */
async function fetchCustomScriptsForPicker(signal, targetObject, options = {}) {
  const matchObjectOnly = options.matchObjectOnly !== false;
  const params = new URLSearchParams();
  if (targetObject != null && String(targetObject).trim() !== '') {
    params.set('targetObject', String(targetObject).trim());
  }
  if (matchObjectOnly) {
    params.set('matchObjectOnly', 'true');
  }
  const qs = params.toString();
  const url = `${API_BASE_URL}/api/custom-scripts${qs ? `?${qs}` : ''}`;
  const res = await axios.get(url, {
    headers: { ...crmAuthHeaders() },
    signal,
  });
  if (!res.data?.success || !Array.isArray(res.data.data)) {
    return [];
  }
  return res.data.data;
}

/** Same as FieldManager: company_subscription / license objectManager.enable_standard ("Enable Standard"). */
const isEntitlementEnabled = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const ActionManager = ({ show, onHide, objectName: propObjectName }) => {
  const { objectName: urlObjectName } = useParams();
  const objectName = propObjectName || urlObjectName;
  const objectManagerConfig = getEntitlementsSection('objectManager', {});
  const allowStandardActionCheckbox =
    isEntitlementEnabled(objectManagerConfig?.enable_standard) ||
    isEntitlementEnabled(objectManagerConfig?.enableStandard);
  /** When Enable Standard is on, standard actions are not editable (same idea as protecting seeded config). */
  const isStandardActionRowLocked = (action) =>
    allowStandardActionCheckbox && !!action?.is_standard;

  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionToDelete, setActionToDelete] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showActionModal, setShowActionModal] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [objects, setObjects] = useState([]);
  const [registryRows, setRegistryRows] = useState([]);
  const [customScriptRows, setCustomScriptRows] = useState([]);
  const [targetObjectFields, setTargetObjectFields] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [actionForm, setActionForm] = useState({
    action_label: '',
    action_name: '',
    icon: '',
    action_type: 'row',
    target_url: '',
    component_name: '',
    is_active: true,
    is_standard: false,
    action_behavior: '',
    open_in_modal: false,
    target_object: '',
    prop_mapping_draft: {},
    custom_script_id: ''
  });

  useEffect(() => {
    if (show) {
      fetchActions();
      fetchObjects();
    }
  }, [show, objectName]);

  useEffect(() => {
    if (!showActionModal || actionForm.action_behavior !== 'component') {
      return undefined;
    }
    const ac = new AbortController();
    const target = (actionForm.target_object || '').trim();
    (async () => {
      try {
        const rows = await fetchUiComponentRegistryRows(ac.signal, target || null, {
          matchObjectOnly: true,
        });
        if (!ac.signal.aborted) {
          setRegistryRows(rows);
        }
      } catch {
        if (!ac.signal.aborted) {
          setRegistryRows([]);
        }
      }
    })();
    return () => ac.abort();
  }, [showActionModal, actionForm.action_behavior, actionForm.target_object]);

  useEffect(() => {
    if (!showActionModal || actionForm.action_behavior !== 'custom_script') {
      return undefined;
    }
    const ac = new AbortController();
    const target = (actionForm.target_object || '').trim();
    (async () => {
      try {
        const rows = await fetchCustomScriptsForPicker(ac.signal, target || null, {
          matchObjectOnly: true,
        });
        if (!ac.signal.aborted) {
          setCustomScriptRows(rows);
        }
      } catch {
        if (!ac.signal.aborted) {
          setCustomScriptRows([]);
        }
      }
    })();
    return () => ac.abort();
  }, [showActionModal, actionForm.action_behavior, actionForm.target_object]);

  useEffect(() => {
    if (!showActionModal || actionForm.action_behavior !== 'component') {
      setTargetObjectFields([]);
      return undefined;
    }
    const to = (actionForm.target_object || '').trim();
    if (!to) {
      setTargetObjectFields([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const api = new DataApi(`objectconfig/${to}`);
        const response = await api.fetchAll();
        if (cancelled) return;
        const data = response?.data?.data;
        const fields = data?.fields && Array.isArray(data.fields) ? data.fields : [];
        setTargetObjectFields(fields);
      } catch (e) {
        if (!cancelled) {
          console.error('ActionManager: object fields', e);
          setTargetObjectFields([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showActionModal, actionForm.action_behavior, actionForm.target_object]);

  const selectedRegistryRow = useMemo(
    () => registryRows.find((r) => r.component_key === actionForm.component_name),
    [registryRows, actionForm.component_name]
  );

  const propSpecNeedsTargetFields = useMemo(() => {
    const specs = selectedRegistryRow?.config?.propSpec;
    if (!Array.isArray(specs)) return false;
    return specs.some((s) => propSpecSourceType(s) === 'field');
  }, [selectedRegistryRow?.config?.propSpec]);

  const targetObjectFieldOptions = useMemo(() => {
    const to = (actionForm.target_object || '').trim();
    if (!to || !Array.isArray(targetObjectFields) || targetObjectFields.length === 0) {
      return [];
    }
    return targetObjectFields
      .filter((f) => f.field_name && !f.is_deleted)
      .map((f) => ({
        value: `${to}.${f.field_name}`,
        label: `${f.field_label || f.field_name} (${to}.${f.field_name})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [actionForm.target_object, targetObjectFields]);

  /** Normalize legacy propMappings values like "id" to "object.id" when options load. */
  useEffect(() => {
    const to = (actionForm.target_object || '').trim();
    if (!to || targetObjectFieldOptions.length === 0) return;
    const propSpecs = selectedRegistryRow?.config?.propSpec;
    if (!Array.isArray(propSpecs) || propSpecs.length === 0) return;
    setActionForm((prev) => {
      let changed = false;
      const nextDraft = { ...prev.prop_mapping_draft };
      for (const spec of propSpecs) {
        if (propSpecSourceType(spec) !== 'field') continue;
        const p = spec?.prop;
        if (!p) continue;
        let v = nextDraft[p];
        if (v == null || v === '') continue;
        v = String(v);
        if (!v.includes('.')) {
          const dotted = `${to}.${v}`;
          if (targetObjectFieldOptions.some((o) => o.value === dotted)) {
            nextDraft[p] = dotted;
            changed = true;
          }
        }
      }
      return changed ? { ...prev, prop_mapping_draft: nextDraft } : prev;
    });
  }, [
    actionForm.target_object,
    targetObjectFieldOptions,
    selectedRegistryRow?.config?.propSpec,
    selectedRegistryRow?.component_key,
  ]);

  const fetchActions = async () => {
    try {
      setLoading(true);
      setError('');

      const api = new DataApi('actions');
      const response = await api.fetchAll();

      if (response?.data?.success) {
        let allActions = response.data.data || [];

        // Filter actions by objectName if provided
        if (objectName) {
          allActions = allActions.filter(action => action.target_object === objectName);
        }

        setActions(allActions);
      } else {
        setToast({ show: true, message: 'Failed to load actions', type: 'error' });
      }
    } catch (error) {
      console.error('Error fetching actions:', error);
      const errorMessage = error.message || 'An unexpected error occurred while loading actions';
      setToast({ show: true, message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchObjects = async () => {
    try {
      const api = new DataApi('objectconfig');
      const response = await api.fetchAll();

      if (response?.data?.success) {
        const objectsWithFields = response.data.data || [];
        const allowed = filterObjectManagerAllowedObjects(objectsWithFields).filter(
          (obj) => obj.object_name !== 'attachment'
        );
        const activeObjects = allowed
          .map((obj) => ({
            value: obj.object_name,
            label: obj.object_label || obj.object_name,
          }))
          .sort((a, b) => {
            const byLabel = String(a.label).localeCompare(String(b.label), undefined, {
              sensitivity: 'base',
            });
            if (byLabel !== 0) return byLabel;
            return String(a.value).localeCompare(String(b.value), undefined, {
              sensitivity: 'base',
            });
          });
        setObjects(activeObjects);
      }
    } catch (error) {
      console.error('Error fetching objects:', error);
    }
  };


  const generateActionName = (label) => {
    if (!label) return '';

    // Convert to lowercase, replace spaces and special characters with underscores
    return label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces and numbers
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with single underscore
      .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
  };

  const handleActionLabelChange = (value) => {
    const generatedName = generateActionName(value);
    setActionForm({
      ...actionForm,
      action_label: value,
      action_name: generatedName
    });
  };

  const resetForm = () => {
    setEditingAction(null);
    setActionForm({
      action_label: '',
      action_name: '',
      icon: '',
      action_type: 'row',
      target_url: '',
      component_name: '',
      is_active: true,
      is_standard: false,
      action_behavior: '',
      open_in_modal: false,
      target_object: objectName || '',
      prop_mapping_draft: {},
      custom_script_id: ''
    });
  };

  const handleCreateAction = () => {
    resetForm();
    setShowActionModal(true);
  };

  const handleEditAction = (action) => {
    if (isStandardActionRowLocked(action)) {
      setToast({
        show: true,
        message:
          'Standard actions cannot be edited while Enable Standard is on for your subscription.',
        type: 'warning',
      });
      return;
    }
    setEditingAction(action);
    setActionForm({
      action_label: action.action_label || '',
      action_name: action.action_name || '',
      icon: action.icon || '',
      action_type: action.action_type || 'row',
      target_url: action.target_url || '',
      component_name: action.component_name || '',
      is_active: action.is_active !== false,
      is_standard: action.is_standard || false,
      action_behavior: action.action_behavior || '',
      open_in_modal: action.open_in_modal || false,
      target_object: action.target_object || '',
      prop_mapping_draft: propMappingsToDraft(
        action.config && typeof action.config === 'object' ? action.config.propMappings : null
      ),
      custom_script_id: (() => {
        const c = action.config && typeof action.config === 'object' ? action.config : null;
        if (!c) return '';
        return String(c.customScriptId || c.custom_script_id || '').trim();
      })()
    });
    setShowActionModal(true);
  };

  const handleSaveAction = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setToast({ show: false, message: '', type: 'success' });

      if (editingAction && isStandardActionRowLocked(editingAction)) {
        setToast({
          show: true,
          message:
            'Standard actions cannot be updated while Enable Standard is on for your subscription.',
          type: 'error',
        });
        setLoading(false);
        return;
      }

      // Validation
      if (!actionForm.action_label.trim()) {
        setToast({ show: true, message: 'Action label is required', type: 'error' });
        return;
      }

      // Generate action name if it's empty (shouldn't happen with auto-generation, but as fallback)
      const finalActionName = actionForm.action_name.trim() || generateActionName(actionForm.action_label);
      if (!finalActionName) {
        setToast({ show: true, message: 'Action name could not be generated from label', type: 'error' });
        return;
      }

      if (actionForm.action_behavior === 'component' && !String(actionForm.component_name || '').trim()) {
        setToast({ show: true, message: 'Component is required', type: 'error' });
        return;
      }

      if (actionForm.action_behavior === 'custom_script') {
        if (!String(actionForm.target_object || '').trim()) {
          setToast({
            show: true,
            message: 'Target object is required to attach a custom script.',
            type: 'error',
          });
          return;
        }
        if (!String(actionForm.custom_script_id || '').trim()) {
          setToast({ show: true, message: 'Select a custom script.', type: 'error' });
          return;
        }
      }

      let parsedConfig = null;

      if (actionForm.action_behavior === 'component') {
        const propSpecs = selectedRegistryRow?.config?.propSpec;
        const hasPropSpecs = Array.isArray(propSpecs) && propSpecs.length > 0;
        const pmFromRows = {};
        if (hasPropSpecs) {
          for (const spec of propSpecs) {
            const p = spec?.prop;
            if (!p) continue;
            const raw = String(actionForm.prop_mapping_draft[p] || '').trim();
            if (!raw) continue;
            if (propSpecSourceType(spec) === 'text') {
              pmFromRows[p] = { sourceType: 'text', value: raw };
            } else {
              pmFromRows[p] = { sourceType: 'field', value: raw };
            }
          }
        } else {
          Object.entries(actionForm.prop_mapping_draft || {}).forEach(([k, v]) => {
            const t = String(v || '').trim();
            if (t) pmFromRows[k] = t;
          });
        }
        if (Object.keys(pmFromRows).length > 0) {
          parsedConfig = { propMappings: pmFromRows };
        }
      } else if (actionForm.action_behavior === 'custom_script') {
        const sid = String(actionForm.custom_script_id || '').trim();
        const row = customScriptRows.find((r) => String(r.id) === sid);
        const prevCfg =
          editingAction?.config && typeof editingAction.config === 'object'
            ? editingAction.config
            : null;
        const scriptKey =
          row?.script_key ||
          (prevCfg && String(prevCfg.scriptKey || prevCfg.script_key || '').trim()) ||
          '';
        if (sid && scriptKey) {
          parsedConfig = { customScriptId: sid, scriptKey };
        } else if (sid) {
          setToast({
            show: true,
            message: 'Could not resolve script key. Wait for the list to load or re-select the script.',
            type: 'error',
          });
          setLoading(false);
          return;
        }
      }

      const formData = {
        action_label: actionForm.action_label,
        action_name: finalActionName,
        icon: actionForm.icon,
        action_type: actionForm.action_type,
        target_url: actionForm.action_behavior === 'url' ? actionForm.target_url : '',
        component_name: actionForm.action_behavior === 'component' ? actionForm.component_name : '',
        is_active: actionForm.is_active,
        is_standard: actionForm.is_standard,
        action_behavior: actionForm.action_behavior,
        open_in_modal: actionForm.open_in_modal,
        target_object: actionForm.target_object,
        config: parsedConfig
      };

      const api = new DataApi('actions');
      let response;

      if (editingAction) {
        response = await api.update(formData, editingAction.id);
      } else {
        response = await api.create(formData);
      }

      if (response?.data?.success) {
        const toastMessage = `Action ${editingAction ? 'updated' : 'created'} successfully!`;
        console.log('Setting success toast:', toastMessage);
        setToast({
          show: true,
          message: toastMessage,
          type: 'success'
        });
        setShowActionModal(false);
        fetchActions();
        resetForm();
      } else {
        const errorMessage = response?.data?.message || 'Failed to save action';
        console.log('Setting error toast:', errorMessage);
        setToast({
          show: true,
          message: errorMessage,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving action:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred while saving the action';
      setToast({ show: true, message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAction = (action) => {
    if (isStandardActionRowLocked(action)) {
      setToast({
        show: true,
        message:
          'Standard actions cannot be deleted while Enable Standard is on for your subscription.',
        type: 'warning',
      });
      return;
    }
    setActionToDelete(action);
    setShowDeleteModal(true);
  };

  const confirmDeleteAction = async () => {
    if (!actionToDelete) return;

    try {
      setLoading(true);
      setToast({ show: false, message: '', type: 'success' });

      const api = new DataApi('actions');
      const response = await api.delete(actionToDelete.id);

      if (response?.data?.success) {
        setActions(actions.filter(a => a.id !== actionToDelete.id));
        setToast({ show: true, message: 'Action deleted successfully!', type: 'success' });
      } else {
        setToast({ show: true, message: 'Failed to delete action', type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting action:', error);
      const errorMessage = error.message || 'An unexpected error occurred while deleting the action';
      setToast({ show: true, message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
      setActionToDelete(null);
    }
  };

  const targetObjectLabelByApiName = useMemo(() => {
    const m = new Map();
    objects.forEach((o) => {
      m.set(String(o.value).toLowerCase(), o.label);
    });
    return m;
  }, [objects]);

  const getTargetObjectDisplayLabel = (apiName) => {
    if (apiName == null || String(apiName).trim() === '') return null;
    const key = String(apiName).trim().toLowerCase();
    return targetObjectLabelByApiName.get(key) || apiName;
  };

  const getActionTypeBadge = (type) => {
    const k = String(type || '').trim().toLowerCase();
    const label = labelForActionType(type);
    const variants = {
      row_action: 'primary',
      list_action: 'secondary',
      detail_action: 'info',
      edit_action: 'warning',
      bulk_action: 'success',
      global_action: 'dark',
      row: 'primary',
      bulk: 'success',
      page: 'info',
      global: 'warning',
    };
    return <Badge bg={variants[k] || 'secondary'}>{label}</Badge>;
  };

  const getActionIcon = (icon) => {
    if (!icon) return <FaBolt className="text-muted" />;

    // Common icon mappings
    const iconMap = {
      'view': '👁️',
      'edit': '✏️',
      'delete': '🗑️',
      'export': '📤',
      'import': '📥',
      'print': '🖨️',
      'email': '📧',
      'download': '⬇️',
      'upload': '⬆️',
      'copy': '📋',
      'share': '🔗'
    };

    return iconMap[icon.toLowerCase()] || icon;
  };

  // Filter actions based on search term and type
  const filteredActions = actions.filter(action => {
    const matchesSearch = action.action_label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      action.action_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (action.component_name && action.component_name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesType = filterType === 'all' || action.action_type === filterType;

    return matchesSearch && matchesType;
  });

  const renderActionModal = () => (
    <Modal show={showActionModal} onHide={() => setShowActionModal(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editingAction ? 'Edit Action' : 'Create New Action'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Action Label *</Form.Label>
                <Form.Control
                  type="text"
                  value={actionForm.action_label}
                  onChange={(e) => handleActionLabelChange(e.target.value)}
                  placeholder="e.g., View Record"
                />
                <Form.Text className="text-muted">
                  Display name for the action
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Action Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={actionForm.action_name}
                  readOnly
                  className="bg-light"
                  placeholder="e.g., view_record"
                />
                <Form.Text className="text-muted">
                  <FaCheck className="me-1 text-success" />
                  Auto-generated from action label (lowercase, underscores)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Icon</Form.Label>
                <Form.Control
                  type="text"
                  value={actionForm.icon}
                  onChange={(e) => setActionForm({ ...actionForm, icon: e.target.value })}
                  placeholder="e.g., view, edit, delete"
                />
                <Form.Text className="text-muted">
                  Icon reference or emoji
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Action Type</Form.Label>
                <Form.Select
                  value={actionForm.action_type}
                  onChange={(e) => setActionForm({ ...actionForm, action_type: e.target.value })}
                >
                  <option value="row_action">Row Action</option>
                  <option value="list_action">List Action</option>
                  <option value="detail_action">Detail Action</option>
                  <option value="edit_action">Edit Action</option>
                  <option value="bulk_action">Bulk Action</option>
                  <option value="global_action">Global Action</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Where this action appears (list row, detail header, etc.)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Target Object</Form.Label>
                <Form.Select
                  value={actionForm.target_object}
                  onChange={(e) => {
                    const v = e.target.value;
                    setActionForm((prev) => ({
                      ...prev,
                      target_object: v,
                      ...(prev.action_behavior === 'component'
                        ? { component_name: '', prop_mapping_draft: {} }
                        : {}),
                      ...(prev.action_behavior === 'custom_script' ? { custom_script_id: '' } : {})
                    }));
                  }}
                >
                  <option value="">Select an object (optional)</option>
                  {objects.map((obj) => (
                    <option key={obj.value} value={obj.value}>
                      {obj.label}
                    </option>
                  ))}
                  {actionForm.target_object &&
                    !objects.some((o) => o.value === actionForm.target_object) && (
                      <option value={actionForm.target_object}>
                        {actionForm.target_object} (current — not in allowlist)
                      </option>
                    )}
                </Form.Select>
                <Form.Text className="text-muted">
                  {actionForm.action_behavior === 'custom_script'
                    ? 'Required for custom script actions — lists only scripts scoped to this object.'
                    : 'Only objects allowed for this tenant (same rules as Object Manager) and with explore enabled.'}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Action Behavior</Form.Label>
                <Form.Select
                  value={actionForm.action_behavior}
                  onChange={(e) => {
                    const v = e.target.value;
                    setActionForm((prev) => ({
                      ...prev,
                      action_behavior: v,
                      ...(v !== 'component' ? { prop_mapping_draft: {} } : {}),
                      ...(v !== 'component' ? { component_name: '' } : {}),
                      ...(v !== 'custom_script' ? { custom_script_id: '' } : {}),
                      ...(v !== 'url' ? { target_url: '' } : {})
                    }));
                  }}
                >
                  <option value="">Select action behavior (optional)</option>
                  <option value="new">New</option>
                  <option value="edit">Edit</option>
                  <option value="delete">Delete</option>
                  <option value="import">Import</option>
                  <option value="export">Export</option>
                  <option value="url">URL</option>
                  <option value="component">Component</option>
                  <option value="custom_script">Custom Script</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  New, edit, URL, component, custom script from the library, etc.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {actionForm.action_behavior === 'custom_script' && (
            <Row>
              <Col md={12}>
                {!actionForm.target_object?.trim() ? (
                  <Alert variant="warning" className="py-2 small">
                    Select a <strong>target object</strong> first. Only <code>custom_script</code> rows
                    with that object in <code>target_object</code> appear here.
                  </Alert>
                ) : null}
                <Form.Group className="mb-3">
                  <Form.Label>Script for this object</Form.Label>
                  <Form.Select
                    value={actionForm.custom_script_id}
                    onChange={(e) =>
                      setActionForm((prev) => ({ ...prev, custom_script_id: e.target.value }))
                    }
                    required
                    disabled={!actionForm.target_object?.trim()}
                  >
                    <option value="">
                      {!actionForm.target_object?.trim()
                        ? 'Select target object above…'
                        : customScriptRows.length === 0
                          ? 'No scripts for this object — add rows in custom_script'
                          : 'Select a script…'}
                    </option>
                    {customScriptRows.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.display_label
                          ? `${r.display_label} (${r.script_key})`
                          : r.script_key}
                      </option>
                    ))}
                    {actionForm.custom_script_id &&
                      !customScriptRows.some(
                        (r) => String(r.id) === String(actionForm.custom_script_id)
                      ) && (
                        <option value={actionForm.custom_script_id}>
                          Current selection (reload or check target object)
                        </option>
                      )}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Active scripts from <code>custom_script</code> where{' '}
                    <code>target_object</code> matches the target object above.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Show Target URL field only when action_behavior is 'url' */}
          {actionForm.action_behavior === 'url' && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Target URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={actionForm.target_url}
                    onChange={(e) => setActionForm({ ...actionForm, target_url: e.target.value })}
                    placeholder="e.g., /records/view"
                    required
                  />
                  <Form.Text className="text-muted">
                    Navigation URL for this action
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                {/* Empty column for layout balance */}
              </Col>
            </Row>
          )}

          {/* Show Component Name field only when action_behavior is 'component' */}
          {actionForm.action_behavior === 'component' && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Component Name</Form.Label>
                  <Form.Select
                    value={actionForm.component_name}
                    onChange={(e) =>
                      setActionForm((prev) => ({
                        ...prev,
                        component_name: e.target.value,
                        prop_mapping_draft: {},
                      }))
                    }
                    required
                  >
                    <option value="">Select a component…</option>
                    {registryRows.map((r) => (
                      <option key={r.component_key} value={r.component_key}>
                        {r.display_label
                          ? `${r.display_label} (${r.component_key})`
                          : r.component_key}
                      </option>
                    ))}
                    {actionForm.component_name &&
                      !registryRows.some((r) => r.component_key === actionForm.component_name) && (
                        <option value={actionForm.component_name}>
                          {actionForm.component_name} (current — not in registry for this object)
                        </option>
                      )}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Components registered for this target object only (universal entries with no object
                    are not listed). Choose a target object first.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                {/* Empty column for layout balance */}
              </Col>
            </Row>
          )}

          {actionForm.action_behavior === 'component' &&
            Array.isArray(selectedRegistryRow?.config?.propSpec) &&
            selectedRegistryRow.config.propSpec.length > 0 && (
              <Row className="mt-2">
                <Col md={12}>
                  <Form.Label className="fw-semibold">Component parameter values</Form.Label>
                  <Form.Text className="text-muted d-block mb-2">
                    <strong>Field</strong>: choose a column on the target object (stored as{' '}
                    <code>object.field</code> with <code>sourceType: &quot;field&quot;</code>
                    ). <strong>Text</strong>: enter a fixed value (<code>sourceType: &quot;text&quot;</code>
                    ).
                  </Form.Text>
                  {propSpecNeedsTargetFields && !actionForm.target_object?.trim() && (
                    <Alert variant="warning" className="py-2 small">
                      Select a <strong>target object</strong> so field-type parameters can use the field
                      dropdown.
                    </Alert>
                  )}
                  <Table bordered size="sm" className="mb-3 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '34%' }}>Parameter</th>
                        <th style={{ width: '12%' }}>Kind</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRegistryRow.config.propSpec.map((spec) => {
                        const propKey = spec?.prop;
                        if (!propKey) return null;
                        const kind = propSpecSourceType(spec);
                        const currentVal = actionForm.prop_mapping_draft[propKey] || '';
                        const hasCurrentExtra =
                          kind === 'field' &&
                          currentVal &&
                          !targetObjectFieldOptions.some((o) => o.value === currentVal);
                        return (
                          <tr key={propKey}>
                            <td>
                              <div className="fw-medium">{spec.label || propKey}</div>
                              {spec.description && (
                                <div className="small text-muted">{spec.description}</div>
                              )}
                              <code className="small text-secondary">{propKey}</code>
                            </td>
                            <td>
                              <Badge bg={kind === 'text' ? 'secondary' : 'primary'}>
                                {kind === 'text' ? 'Text' : 'Field'}
                              </Badge>
                            </td>
                            <td>
                              {kind === 'text' ? (
                                <Form.Control
                                  type="text"
                                  value={currentVal}
                                  onChange={(e) =>
                                    setActionForm((prev) => ({
                                      ...prev,
                                      prop_mapping_draft: {
                                        ...prev.prop_mapping_draft,
                                        [propKey]: e.target.value,
                                      },
                                    }))
                                  }
                                  placeholder="Static value"
                                />
                              ) : !actionForm.target_object?.trim() ? (
                                <span className="text-muted small">Select target object first</span>
                              ) : targetObjectFieldOptions.length === 0 ? (
                                <span className="text-muted small">
                                  Loading fields for {actionForm.target_object}…
                                </span>
                              ) : (
                                <Form.Select
                                  value={currentVal}
                                  onChange={(e) =>
                                    setActionForm((prev) => ({
                                      ...prev,
                                      prop_mapping_draft: {
                                        ...prev.prop_mapping_draft,
                                        [propKey]: e.target.value,
                                      },
                                    }))
                                  }
                                >
                                  <option value="">Select field…</option>
                                  {hasCurrentExtra && (
                                    <option value={currentVal}>
                                      {currentVal} (current value)
                                    </option>
                                  )}
                                  {targetObjectFieldOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            )}

          <Row>
            <Col md={6}>
              <Form.Check
                type="checkbox"
                label="Active"
                checked={actionForm.is_active}
                onChange={(e) => setActionForm({ ...actionForm, is_active: e.target.checked })}
              />
            </Col>
            {allowStandardActionCheckbox && (
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Standard Action"
                  checked={actionForm.is_standard}
                  onChange={(e) => setActionForm({ ...actionForm, is_standard: e.target.checked })}
                />
              </Col>
            )}
          </Row>

          <Row>
            <Col md={6}>
              <Form.Check
                type="checkbox"
                label="Open in Modal"
                checked={actionForm.open_in_modal}
                onChange={(e) => setActionForm({ ...actionForm, open_in_modal: e.target.checked })}
              />
            </Col>
            <Col md={6}>
              {/* Empty column for layout balance */}
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowActionModal(false)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveAction}
          disabled={loading || !actionForm.action_label.trim()}
        >
          {loading ? 'Saving...' : (editingAction ? 'Update Action' : 'Create Action')}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  console.log('ActionManager render - toast state:', toast);

  return (
    <div className="action-manager">
      <SalesforceToast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => {
          console.log('Toast onClose called');
          setToast({ show: false, message: '', type: 'success' });
        }}
        duration={5000}
        position="top-center"
      />

      <div className="action-manager-header">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4>
              <FaBolt className="me-2" />
              {objectName ? `Action Manager - ${objectName}` : 'Action Manager'}
            </h4>
            <p className="text-muted">
              {objectName
                ? `Create and manage actions for ${objectName}`
                : 'Create and manage system actions'
              }
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                console.log('Test toast button clicked');
                setToast({ show: true, message: 'Test toast message!', type: 'success' });
              }}
            >
              Test Toast
            </Button>
            <Button variant="primary" onClick={handleCreateAction}>
              <FaPlus className="me-1" />
              Create Action
            </Button>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search actions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="row">Row Actions</option>
                <option value="bulk">Bulk Actions</option>
                <option value="page">Page Actions</option>
                <option value="global">Global Actions</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <div className="d-flex align-items-center">
                <FaFilter className="me-2 text-muted" />
                <span className="text-muted">
                  {filteredActions.length} of {actions.length} actions
                </span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading && actions.length === 0 ? (
        <div className="text-center py-4">
          <div className="loading-spinner mb-3"></div>
          <p>Loading actions...</p>
        </div>
      ) : filteredActions.length === 0 ? (
        <Card className="empty-state">
          <Card.Body className="text-center py-5">
            <FaBolt size={48} className="text-muted mb-3" />
            <h6>No Actions Found</h6>
            <p className="text-muted mb-3">
              {actions.length === 0
                ? 'Create your first action to get started.'
                : 'No actions match your search criteria. Try adjusting your filters.'
              }
            </p>
            {actions.length === 0 && (
              <Button variant="primary" onClick={handleCreateAction}>
                <FaPlus className="me-1" />
                Create First Action
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Actions List</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Icon</th>
                  <th>Action Label</th>
                  <th>Action Name</th>
                  <th>Action Type</th>
                  <th>Target Object</th>
                  <th>Action Behavior</th>
                  <th>Action Value</th>
                  <th>Status</th>
                  <th>Standard</th>
                  <th>Modal</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredActions.map(action => (
                  <tr key={action.id}>
                    <td>
                      <span className="action-icon">
                        {getActionIcon(action.icon)}
                      </span>
                    </td>
                    <td>
                      <strong>{action.action_label}</strong>
                    </td>
                    <td>
                      <code className="text-muted">{action.action_name}</code>
                    </td>
                    <td>
                      {getActionTypeBadge(action.action_type)}
                    </td>
                    <td>
                      {action.target_object ? (
                        <Badge bg="info">{getTargetObjectDisplayLabel(action.target_object)}</Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {action.action_behavior ? (
                        <Badge bg="primary">{labelForActionBehavior(action.action_behavior)}</Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-break">
                      {(() => {
                        const b = String(action.action_behavior || '').toLowerCase();
                        if (b === 'url' && action.target_url) {
                          return <span className="text-info">{action.target_url}</span>;
                        }
                        if (b === 'component' && action.component_name) {
                          return <span className="text-primary">{action.component_name}</span>;
                        }
                        if (b === 'custom_script') {
                          const sk =
                            action.config &&
                            typeof action.config === 'object' &&
                            (action.config.scriptKey || action.config.script_key)
                              ? String(action.config.scriptKey || action.config.script_key)
                              : '';
                          if (sk) {
                            return <code className="small text-secondary">{sk}</code>;
                          }
                        }
                        return <span className="text-muted">—</span>;
                      })()}
                    </td>
                    <td>
                      <Badge bg={action.is_active ? 'success' : 'secondary'}>
                        {action.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <Badge
                        bg={action.is_standard ? 'warning' : 'info'}
                        text={action.is_standard ? 'dark' : 'white'}
                      >
                        {action.is_standard ? 'Standard' : 'Custom'}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={action.open_in_modal ? 'success' : 'secondary'}>
                        {action.open_in_modal ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditAction(action)}
                          title="Edit Action"
                          disabled={isStandardActionRowLocked(action)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteAction(action)}
                          title="Delete Action"
                          disabled={isStandardActionRowLocked(action)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {renderActionModal()}

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setActionToDelete(null);
        }}
        onConfirm={confirmDeleteAction}
        title="Confirm Delete"
        message="Are you sure you want to delete this action?"
        itemName={actionToDelete?.action_label}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default ActionManager;
